"use client"

import { Button, Card, Col, Row, Select, Skeleton } from "antd"
import ArrowLeftRightIconDownIcon from "../../../icons/ArrowLeftRightIcon"
import { useEffect, useMemo, useRef } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons"
import SelectPeople from "../../FormSearch/desktop/SelectPeople"
import SelectCabinClass from "../../FormSearch/desktop/SelectCabinClass"
import SelectAirportFrom from "../../FormSearch/desktop/SelectAirportFrom"
import SelectAirportTo from "../../FormSearch/desktop/SelectAirportTo"
import { useAppDispatch, useAppSelector } from "@/lib/redux/hooks"
import { getFlightsAllAirlines } from "@/utils/flight/getFlightsAllAirlines"
import { flightActions } from "@/lib/redux/features/flight/reduce"
import SelectDateFromTo from "../../FormSearch/desktop/SelectDateFromTo"
import { useTERouter } from "@/hooks/app/useTERouter"
import { useTranslation } from "react-i18next"
import { FlightSearchPrefill } from "../landing/flightSearchPrefill"
import dayjs from "dayjs"
import SelectAirportFromSkeleton from "../../FormSearch/desktop/SelectAirportFrom.skeleton"
import SelectDateFromToSkeleton from "../../FormSearch/desktop/SelectDateFromTo.skeleton"

type FormSearchFlightProps = {
  fixedPosition?: boolean
  prefill?: FlightSearchPrefill
}

const FormSearchFlightSkeleton = (props: FormSearchFlightProps = { fixedPosition: true }) => {
  const { t, i18n } = useTranslation()
  const ref = useRef<HTMLDivElement>(null)
  const dispatch = useAppDispatch()
  const router = useTERouter()
  const checkedItinerary = useAppSelector(state => state.flight.checkedItinerary)
  const inputFlights = useAppSelector(state => state.flight.inputFlights)
  const isMultiCities = useAppSelector(state => state.flight.isMultiCities)
  const airports = useAppSelector((state) => state.flight.get_airports.data)
  const didPrefillRef = useRef(false)
  const observer = useMemo(() => typeof IntersectionObserver === 'undefined' ? {} as any : new IntersectionObserver(
    ([e]) => isMultiCities ? {} : e.target.classList.toggle('sticking', e.intersectionRatio < 1),
    { threshold: [1] }
  ), [isMultiCities]);

  useEffect(() => {
    if (!props.prefill) return
    if (didPrefillRef.current) return

    const hasAirports = airports && Object.keys(airports).length > 0
    if (!hasAirports) return

    const normalizeCode = (value?: string) => String(value || "").trim().toUpperCase()
    const startCode = normalizeCode(props.prefill.startPoint ?? undefined)
    const endCode = normalizeCode(props.prefill.endPoint ?? undefined)

    const findAirport = (code: string) => {
      if (!code) return null
      return (airports as any)?.[code] || (Object.values(airports as any).find((a: any) => a?.code === code) as any)
    }

    dispatch(flightActions.updateModeSearchFlight({ isMultiCities: false }))
    dispatch(flightActions.changeCheckedItinerary(false))

    // DES_ROUTE: only prefill destination, avoid showing default origin (SGN)
    if (!startCode && !!endCode) {
      dispatch(
        flightActions.selectFromAirport({
          indexInputFlight: 0,
          fromAirport: undefined as any,
        })
      )
    }

    if (startCode) {
      const fromAirport = findAirport(startCode)
      if (fromAirport) dispatch(flightActions.selectFromAirport({ indexInputFlight: 0, fromAirport }))
    }

    if (endCode) {
      const toAirport = findAirport(endCode)
      if (toAirport) dispatch(flightActions.selectToAirport({ indexInputFlight: 0, toAirport }))
    }

    if (props.prefill.dateFrom) {
      const d = dayjs(props.prefill.dateFrom)
      dispatch(
        flightActions.changeDateFrom({
          indexInputFlight: 0,
          dateFrom: (d.isValid() ? d : dayjs()).format("YYYY-MM-DD"),
        })
      )
    }

    didPrefillRef.current = true
  }, [airports, dispatch, props.prefill])

  useEffect(() => {
    if (ref.current) {
      observer.observe(ref.current)
    }
    return () => {
      if (ref.current) {
        observer.unobserve(ref.current)
      }
    }
  }, [observer])

  return (
    <Row style={{ marginTop: 15, zIndex: 3 }} ref={ref} justify='center' className={`flights__formsearch${isMultiCities ? " flights__formsearch--multi" : ""}${!!props.fixedPosition ? "" : " flights__formsearch--disabled-fixed-potision"}`}>
      <Col span={24} className="flights__formsearch--box">
        <Card className="flights__formsearch--content" variant='borderless' size='small'>
          <Row justify='space-between' align='middle' style={{ marginBottom: '1rem', marginInline: '-0.75rem' }}>
            <Col style={{ paddingInline: '0.75rem' }}>
              <Row gutter={5} wrap={false}>
                <Col>
                  <Skeleton.Button
                    style={{ minWidth: '15rem' }}
                    active
                  />
                </Col>
                <Col>
                  <Skeleton.Button
                    style={{ minWidth: '30.2rem' }}
                    active
                  />
                </Col>
                <Col>
                  <Skeleton.Button
                    style={{ minWidth: '12.6rem' }}
                    active
                  />
                </Col>
              </Row>
            </Col>
          </Row>
          {[0].map((input, index) => {
            return (
              <Row key={index} wrap={false} gutter={15} style={index < (inputFlights.length - 1) ? { marginBottom: '1rem', marginInline: '-0.75rem' } : { marginInline: '-0.75rem' }}>
                <Col flex={1} style={{ paddingInline: '0.75rem' }}>
                  <Row style={{ marginInline: '-0.75rem' }}>
                    <Col span={isMultiCities ? 16 : 12} style={{ paddingInline: '0.75rem' }}>
                      <Row gutter={10} wrap={false}>
                        <Col flex={1}>
                          <SelectAirportFromSkeleton
                            size="small"
                          />
                        </Col>
                        <Col>
                          <Row align='middle' style={{ height: '6.857rem' }}>
                            <Skeleton.Button
                              active
                              styles={{
                                content: {
                                  height: '1.6rem',
                                  width: '1.6rem',
                                  minWidth: '1.6rem'
                                }
                              }}

                            />
                          </Row>
                          {/* <Row style={{ height: 55 }}></Row> */}
                        </Col>
                        <Col flex={1}>
                          <Row>
                            <SelectAirportFromSkeleton
                              size="small"
                            />
                          </Row>
                        </Col>
                      </Row>
                    </Col>
                    <Col span={isMultiCities ? 8 : 12} style={{ paddingInline: '0.75rem' }}>
                      <SelectDateFromToSkeleton size='small' />
                    </Col>
                  </Row>
                </Col>
                <Col style={{ paddingInline: '0.75rem' }}>
                    <Skeleton.Button
                        size='large'
                        style={{
                          height: '6.835rem',
                          width: '6.835rem'
                        }}
                        active
                      />
                  </Col>
              </Row>
            )
          })}

        </Card>
      </Col>
    </Row>
  )
}

export default FormSearchFlightSkeleton